// src/utils/bankStatementParsers.js
// Smart bank/credit-card statement parsers with auto-detection.
// Supports: HDFC Bank (India), Rabobank (NL, English or Dutch export), Credit Card (generic).

// ── Shared helpers ─────────────────────────────────────────────────────────

function stripBom(text) {
  return text.replace(/^\uFEFF/, '');
}

function splitLines(text) {
  return text.split(/\r?\n/).filter(l => l.trim());
}

function detectDelimiter(text) {
  const semi  = (text.match(/;/g) || []).length;
  const tab   = (text.match(/\t/g) || []).length;
  const comma = (text.match(/,/g) || []).length;
  if (tab >= semi && tab >= comma) return '\t';
  if (semi >= comma) return ';';
  return ',';
}

function parseEuropeanNumber(str) {
  if (!str) return 0;
  const s = str.trim();
  // Signed European: "+6941,23" or "-50,78"
  if (s.includes(',')) return parseFloat(s.replace(/\./g, '').replace(',', '.')) || 0;
  return parseFloat(s.replace(/[^\d.+-]/g, '')) || 0;
}

function parseDateFlex(str, dateOrder = 'DMY') {
  if (!str) return new Date().toISOString().split('T')[0];
  const s = str.trim();
  // YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  // Two-digit/two-digit/four-digit: use caller-detected order
  const parts = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
  if (parts) {
    const yyyy = parts[3];
    if (dateOrder === 'MDY') {
      // MM/DD/YYYY
      return `${yyyy}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`;
    }
    // DD/MM/YYYY (default)
    return `${yyyy}-${parts[2].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
  }
  // DD-MMM-YYYY (e.g. 15-Jan-2026)
  const dmy2 = s.match(/^(\d{1,2})-(\w{3})-(\d{4})$/);
  if (dmy2) {
    const months = { jan: '01', feb: '02', mar: '03', apr: '04', may: '05', jun: '06',
      jul: '07', aug: '08', sep: '09', oct: '10', nov: '11', dec: '12' };
    const m = months[dmy2[2].toLowerCase()];
    if (m) return `${dmy2[3]}-${m}-${dmy2[1].padStart(2, '0')}`;
  }
  return s;
}

// Scan a list of raw date strings to decide whether they are DD/MM/YYYY ('DMY')
// or MM/DD/YYYY ('MDY').  A single unambiguous date (where one part > 12) is
// enough to determine the whole file's convention.  Defaults to 'DMY'.
function detectDateOrder(dateStrings) {
  for (const s of dateStrings) {
    if (!s) continue;
    const parts = s.trim().match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
    if (!parts) continue;
    const a = parseInt(parts[1], 10);
    const b = parseInt(parts[2], 10);
    if (b > 12) return 'MDY'; // second part can't be a month → MM/DD/YYYY
    if (a > 12) return 'DMY'; // first part can't be a month → DD/MM/YYYY
  }
  return 'DMY'; // default: European / Indian convention
}

function smartSplitCSV(line, delimiter) {
  // Handle quoted fields (fields may contain the delimiter inside quotes)
  const fields = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      inQuotes = !inQuotes;
    } else if (ch === delimiter && !inQuotes) {
      fields.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  fields.push(current.trim());
  return fields;
}

// ── Format detection ───────────────────────────────────────────────────────

const FORMAT_RABOBANK = 'rabobank';
const FORMAT_HDFC     = 'hdfc_bank';
const FORMAT_CC       = 'credit_card';
const FORMAT_UNKNOWN  = 'unknown';

const FORMAT_LABELS = {
  [FORMAT_RABOBANK]: 'Rabobank (NL)',
  [FORMAT_HDFC]:     'HDFC Bank (India)',
  [FORMAT_CC]:       'Credit Card Statement',
  [FORMAT_UNKNOWN]:  'Unknown Format',
};

function detectFormat(text) {
  const lower = text.toLowerCase();
  const firstLines = lower.split(/\r?\n/).slice(0, 15).join(' ');

  // Rabobank: has IBAN/BBAN, BIC, Counterpty, Bal After Bkng
  if (firstLines.includes('iban') && firstLines.includes('counterpty') && firstLines.includes('bic')) {
    return FORMAT_RABOBANK;
  }
  if (firstLines.includes('iban/bban') || (firstLines.includes('bal after bkng') && firstLines.includes('counterpty'))) {
    return FORMAT_RABOBANK;
  }

  // HDFC: has UTR, Beneficiary, Txn
  const hdfcKeywords = ['utr', 'beneficiary', 'txn'];
  const hdfcMatch = hdfcKeywords.filter(kw => firstLines.includes(kw)).length;
  if (hdfcMatch >= 2) return FORMAT_HDFC;

  // Credit card: Datum + Omschrijving + Bedrag (Dutch CC) or Date + Description + Amount with no IBAN
  if ((firstLines.includes('datum') && firstLines.includes('omschrijving') && firstLines.includes('bedrag'))) {
    return FORMAT_CC;
  }
  // English CC fallback
  if (firstLines.includes('date') && firstLines.includes('description') && firstLines.includes('amount')
      && !firstLines.includes('iban') && !firstLines.includes('beneficiary')) {
    return FORMAT_CC;
  }

  return FORMAT_UNKNOWN;
}

// ── Rabobank parser ────────────────────────────────────────────────────────

function parseRabobank(text) {
  const cleanText = stripBom(text);
  const lines = splitLines(cleanText);
  const delimiter = detectDelimiter(cleanText);

  // Find header row. Rabobank exports in English or Dutch depending on the
  // language of the online banking session.
  const headerIdx = lines.findIndex(l => {
    const low = l.toLowerCase();
    return low.includes('iban') && (
      (low.includes('counterpty') && low.includes('amount'))
      || (low.includes('tegenrekening') && low.includes('bedrag'))
    );
  });
  if (headerIdx < 0) return { rows: [], errors: ['Could not find Rabobank header row (expected IBAN/BBAN with Counterpty/Amount or Tegenrekening/Bedrag)'] };

  const headers = smartSplitCSV(lines[headerIdx], delimiter).map(h => h.trim().toLowerCase());
  // Exact header name first: a substring match would read 'Rentedatum' as
  // 'Datum' and 'Oorspr bedrag' as 'Bedrag'. Substring keywords are the
  // fallback for English header variants.
  const findCol = (names, keywords = []) => {
    const exact = headers.findIndex(h => names.includes(h));
    if (exact >= 0) return exact;
    return headers.findIndex(h => keywords.some(k => h.includes(k)));
  };

  const col = {
    date:          findCol(['date', 'datum'], ['date']),
    valueDate:     findCol(['value date', 'rentedatum'], ['value date']),
    amount:        findCol(['amount', 'bedrag'], ['amount']),
    balAfter:      findCol(['bal after bkng', 'saldo na trn'], ['bal after']),
    counterIban:   findCol(['counterpty iban/bban', 'tegenrekening iban/bban'], ['counterpty iban', 'counterpty']),
    counterName:   findCol(['name counterpty', 'naam tegenpartij'], ['name counterpty']),
    paymentRef:    findCol(['payment reference', 'betalingskenmerk'], ['payment reference']),
    description1:  findCol(['description-1', 'omschrijving-1'], ['description-1', 'description1']),
    description2:  findCol(['description-2', 'omschrijving-2'], ['description-2', 'description2']),
    description3:  findCol(['description-3', 'omschrijving-3'], ['description-3', 'description3']),
    txnRef:        findCol(['transaction reference', 'transactiereferentie'], ['transaction reference']),
    ccy:           findCol(['ccy', 'munt'], ['ccy']),
    instrAmt:      findCol(['instr amt', 'oorspr bedrag'], ['instr amt']),
    instrCcy:      findCol(['instr ccy', 'oorspr munt'], ['instr ccy']),
    code:          findCol(['code'], ['code']),
  };

  const rows = [];
  const errors = [];

  // Detect DD/MM vs MM/DD order from all date values before parsing rows
  const rawDates = lines.slice(headerIdx + 1).map(l => {
    try {
      const f = smartSplitCSV(l, delimiter);
      return col.valueDate >= 0 ? f[col.valueDate] : (col.date >= 0 ? f[col.date] : '');
    } catch (_) { return ''; }
  });
  const dateOrder = detectDateOrder(rawDates);

  for (let i = headerIdx + 1; i < lines.length; i++) {
    try {
      const fields = smartSplitCSV(lines[i], delimiter);
      if (fields.filter(f => f).length < 3) continue;

      const amount = parseEuropeanNumber(fields[col.amount] || '');
      const counterName = col.counterName >= 0 ? fields[col.counterName] : '';
      const desc1 = col.description1 >= 0 ? fields[col.description1] : '';
      const desc2 = col.description2 >= 0 ? fields[col.description2] : '';
      const desc3 = col.description3 >= 0 ? fields[col.description3] : '';
      const description = [desc1, desc2, desc3].filter(Boolean).join(' ').trim();
      const dateStr = col.valueDate >= 0 ? fields[col.valueDate] : (col.date >= 0 ? fields[col.date] : '');
      const payRef = col.paymentRef >= 0 ? fields[col.paymentRef] : '';
      const txnRef = col.txnRef >= 0 ? fields[col.txnRef] : '';
      const counterIban = col.counterIban >= 0 ? fields[col.counterIban] : '';
      const ccy = col.ccy >= 0 ? fields[col.ccy] : 'EUR';

      rows.push({
        _lineNum: i + 1,
        date: parseDateFlex(dateStr, dateOrder),
        amount: Math.abs(amount),
        amountSigned: amount,
        isDebit: amount < 0,
        counterparty: counterName,
        counterIban,
        description: description || counterName,
        reference: payRef || txnRef,
        currency: (ccy || 'EUR').toUpperCase().trim(),
        officeLocation: 'NL',
        // User-editable fields
        assignedType: 'brio', // 'brio' | 'personal'
        projectId: null,
        category: '',
        selected: false,
      });
    } catch (err) {
      errors.push(`Line ${i + 1}: ${err.message}`);
    }
  }

  return { rows, errors };
}

// ── HDFC Bank parser ───────────────────────────────────────────────────────

function parseHDFC(text) {
  const cleanText = stripBom(text);
  const lines = splitLines(cleanText);
  const delimiter = detectDelimiter(cleanText);

  // Find header row (bank CSVs have metadata rows before the header)
  const bankHeaderKeywords = ['utr', 'beneficiary', 'txn'];
  let headerIdx = -1;
  for (let i = 0; i < Math.min(lines.length, 10); i++) {
    const lower = lines[i].toLowerCase();
    const matchCount = bankHeaderKeywords.filter(kw => lower.includes(kw)).length;
    if (matchCount >= 2) { headerIdx = i; break; }
  }

  if (headerIdx < 0) {
    // Fallback: find most common column count
    const colCounts = lines.map(l => smartSplitCSV(l, delimiter).length);
    const freq = {};
    colCounts.forEach(c => { freq[c] = (freq[c] || 0) + 1; });
    const dataWidth = Number(Object.entries(freq).sort((a, b) => b[1] - a[1])[0][0]);
    headerIdx = colCounts.indexOf(dataWidth);
  }

  const headers = smartSplitCSV(lines[headerIdx], delimiter).map(h => h.trim().toLowerCase().replace(/[^a-z0-9 ]/g, ''));
  const findCol = (...keywords) => headers.findIndex(h => keywords.some(k => h.includes(k)));

  const col = {
    status:      findCol('status'),
    beneficiary: findCol('beneficiary name', 'beneficiary'),
    amount:      findCol('amount', 'debit', 'credit'),
    debit:       findCol('debit'),
    credit:      findCol('credit'),
    date:        findCol('value date', 'date', 'txn date'),
    utr:         findCol('utr', 'ref no'),
    description: findCol('description', 'remarks', 'narration'),
  };

  const rows = [];
  const errors = [];

  // Detect DD/MM vs MM/DD order from all date values before parsing rows
  const rawDates = lines.slice(headerIdx + 1).map(l => {
    try { const f = smartSplitCSV(l, delimiter); return col.date >= 0 ? f[col.date] : ''; }
    catch (_) { return ''; }
  });
  const dateOrder = detectDateOrder(rawDates);

  for (let i = headerIdx + 1; i < lines.length; i++) {
    try {
      const fields = smartSplitCSV(lines[i], delimiter);
      if (fields.filter(f => f).length < 3) continue;

      const beneficiary = col.beneficiary >= 0 ? fields[col.beneficiary] : '';
      const desc = col.description >= 0 ? fields[col.description] : '';
      const utr = col.utr >= 0 ? fields[col.utr] : '';
      const dateStr = col.date >= 0 ? fields[col.date] : '';
      const txnStatus = col.status >= 0 ? fields[col.status] : '';

      // Amount: try debit/credit columns first, then generic amount
      let amount = 0;
      let isDebit = true;
      if (col.debit >= 0 && fields[col.debit] && parseEuropeanNumber(fields[col.debit]) !== 0) {
        amount = Math.abs(parseEuropeanNumber(fields[col.debit]));
        isDebit = true;
      } else if (col.credit >= 0 && fields[col.credit] && parseEuropeanNumber(fields[col.credit]) !== 0) {
        amount = Math.abs(parseEuropeanNumber(fields[col.credit]));
        isDebit = false;
      } else if (col.amount >= 0) {
        const raw = parseEuropeanNumber(fields[col.amount]);
        amount = Math.abs(raw);
        isDebit = raw < 0;
      }

      if (!beneficiary && !desc) continue;

      rows.push({
        _lineNum: i + 1,
        date: parseDateFlex(dateStr, dateOrder),
        amount,
        amountSigned: isDebit ? -amount : amount,
        isDebit,
        counterparty: beneficiary,
        counterIban: '',
        description: (beneficiary + (desc ? ` - ${desc}` : '') + (utr ? ` (UTR: ${utr})` : '')).trim(),
        reference: utr,
        currency: 'INR',
        officeLocation: 'IN',
        status: txnStatus.toLowerCase() === 'processed' ? 'paid' : (txnStatus.toLowerCase() || 'planned'),
        assignedType: 'brio',
        projectId: null,
        category: '',
        selected: false,
      });
    } catch (err) {
      errors.push(`Line ${i + 1}: ${err.message}`);
    }
  }

  return { rows, errors };
}

// ── Credit Card parser ─────────────────────────────────────────────────────

function parseCreditCard(text) {
  const cleanText = stripBom(text);
  const lines = splitLines(cleanText);
  const delimiter = detectDelimiter(cleanText);

  // Find header
  const headerIdx = lines.findIndex(l => {
    const low = l.toLowerCase();
    return (low.includes('datum') || low.includes('date'))
      && (low.includes('omschrijving') || low.includes('description'))
      && (low.includes('bedrag') || low.includes('amount'));
  });
  if (headerIdx < 0) return { rows: [], errors: ['Could not find credit card header row (expected: Datum/Date, Omschrijving/Description, Bedrag/Amount)'] };

  const headers = smartSplitCSV(lines[headerIdx], delimiter).map(h => h.trim().toLowerCase());
  const findCol = (...keywords) => headers.findIndex(h => keywords.some(k => h.includes(k)));

  const col = {
    date:        findCol('datum', 'date'),
    description: findCol('omschrijving', 'description'),
    amount:      findCol('bedrag', 'amount'),
  };

  const rows = [];
  const errors = [];

  // Detect DD/MM vs MM/DD order from all date values before parsing rows
  const rawDates = lines.slice(headerIdx + 1).map(l => {
    try { const f = smartSplitCSV(l, delimiter); return col.date >= 0 ? f[col.date] : ''; }
    catch (_) { return ''; }
  });
  const dateOrder = detectDateOrder(rawDates);

  for (let i = headerIdx + 1; i < lines.length; i++) {
    try {
      const fields = smartSplitCSV(lines[i], delimiter);
      if (fields.filter(f => f).length < 2) continue;

      const dateStr = col.date >= 0 ? fields[col.date] : '';
      const desc = col.description >= 0 ? fields[col.description] : '';
      const rawAmount = parseEuropeanNumber(col.amount >= 0 ? fields[col.amount] : '');

      if (!desc) continue;

      rows.push({
        _lineNum: i + 1,
        date: parseDateFlex(dateStr, dateOrder),
        amount: Math.abs(rawAmount),
        amountSigned: rawAmount,
        isDebit: rawAmount > 0, // CC: positive = charge
        counterparty: desc,
        counterIban: '',
        description: desc,
        reference: '',
        currency: 'EUR',
        officeLocation: 'NL',
        assignedType: 'brio',
        projectId: null,
        category: '',
        selected: false,
      });
    } catch (err) {
      errors.push(`Line ${i + 1}: ${err.message}`);
    }
  }

  return { rows, errors };
}

// ── Public API ─────────────────────────────────────────────────────────────

// Money coming in (a BTW refund, a client payment, a card refund) is not an
// expense. Every parser keeps `amount` positive and the direction in
// `isDebit`, so the import reads this one check to leave such rows out.
export function isIncomingRow(row) {
  return row?.isDebit === false;
}

export function parseStatement(text) {
  const format = detectFormat(text);
  let result;
  switch (format) {
    case FORMAT_RABOBANK: result = parseRabobank(text); break;
    case FORMAT_HDFC:     result = parseHDFC(text);     break;
    case FORMAT_CC:       result = parseCreditCard(text); break;
    default:              result = { rows: [], errors: ['Could not auto-detect the statement format. Supported formats: Rabobank (NL), HDFC Bank (India), Credit Card (Datum/Omschrijving/Bedrag).'] };
  }
  return { ...result, format, formatLabel: FORMAT_LABELS[format] || format };
}

export { FORMAT_RABOBANK, FORMAT_HDFC, FORMAT_CC, FORMAT_UNKNOWN, FORMAT_LABELS, parseDateFlex, parseEuropeanNumber };
